import React from "react";

import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import { useUserContext } from "contexts/UserContext";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_warning_solid.svg";

const EnterModal = ({ open, row, onClose, onConfirm }) => {
  const { userState } = useUserContext();
  if (!row) return null;

  const originalData = row?.originalData ?? {};

  const scopeType =
    row?.scopeType === "org"
      ? "Organization"
      : row?.scopeType === "site"
        ? "Site"
        : "Site";

  const scopeName =
    originalData?.siteName ??
    originalData?.orgName ??
    originalData?.scopeName ??
    String(row?.customer ?? "-")
      .replace(/^Site:\s*/i, "")
      .replace(/^Organization:\s*/i, "")
      .replace(/^Org:\s*/i, "");

  const supportAlias = userState?.displayName;

  const accessLevel = row?.access ?? "-";

  const formatExpireDate = (value) => {
    if (!value) return row?.expires ?? "-";

    const timestamp =
      typeof value === "number" && value < 100000000000 ? value * 1000 : value;

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return row?.expires ?? "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const expireDate = formatExpireDate(originalData?.expireAt);

  const handleConfirm = () => {
    onConfirm?.(row);
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={onClose}
      aria-labelledby="enter-customer-environment-title"
      aria-describedby="enter-customer-environment-description"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 536,
          maxWidth: "calc(100% - 32px)",
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          overflow: "hidden",
          outline: "none",
          boxShadow: "0 24px 64px rgba(16, 24, 40, 0.22)",
        }}
      >
        <Box
          sx={{
            minHeight: 70,
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #D9DEE8",
          }}
        >
          <Box
            sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}
          >
            <AlertIcon />

            <Typography
              id="enter-customer-environment-title"
              sx={{
                color: "#172B68",
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              Entering a customer environment
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              flexShrink: 0,
              ml: 1,
              color: "#98A2B3",
              "&:hover": { bgcolor: "#F2F4F7" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          id="enter-customer-environment-description"
          sx={{ px: 2.5, py: 2.75 }}
        >
          <Box
            sx={{
              px: 2.75,
              py: 2.25,
              border: "1px solid #FF8A00",
              borderRadius: "9px",
              bgcolor: "#FFFDF8",
            }}
          >
            <Typography
              sx={{ mb: 1, color: "#4A4F5A", fontSize: 15, lineHeight: 1.5 }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {scopeType}: {scopeName}
              </Box>{" "}
              is delegated to you through a customer support token.
            </Typography>

            <Box
              component="ul"
              sx={{
                mt: 0,
                mb: 0,
                pl: 2.5,
                color: "#667085",
                fontSize: 15,
                lineHeight: 1.5,
                "& li": { mb: 0.75 },
                "& li:last-child": { mb: 0 },
              }}
            >
              <li>
                You will act as{" "}
                <Box
                  component="span"
                  sx={{ color: "#344054", fontWeight: 700 }}
                >
                  {supportAlias}
                </Box>
                . Every action is recorded in the customer&apos;s logs under
                this ID and is visible to them.
              </li>

              <li>
                Your{" "}
                <Box
                  component="span"
                  sx={{ color: "#344054", fontWeight: 700 }}
                >
                  real name is never shown
                </Box>{" "}
                to the customer.
              </li>

              <li>
                Access level:{" "}
                <Box
                  component="span"
                  sx={{ color: "#344054", fontWeight: 700 }}
                >
                  {accessLevel}
                </Box>{" "}
                · expires {expireDate}.
              </li>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderTop: "1px solid #D9DEE8",
            bgcolor: "#FBFCFF",
          }}
        >
          <OutlineButton width="80px" height="32px" onClick={onClose}>
            Cancel
          </OutlineButton>
          <SolidButton onClick={handleConfirm} height="32px">
            Enter as {supportAlias}
          </SolidButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default EnterModal;
